import { useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import { openChatStream } from '../services/chat';
import { ContentPart, SendMessageArgs } from '../types/chat';
import { eventToContentPart, parseStreamLine } from '../utils/parsing';

export type UseSendMessageResult = {
  mutation: ReturnType<typeof useMutation<void, unknown, SendMessageArgs>>;
  cancel: () => void;
};

export function useSendMessage(onPart: (part: ContentPart) => void): UseSendMessageResult {
  const abortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  async function streamMessage(params: SendMessageArgs) {
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const reader = await openChatStream(params, controller.signal);
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) return;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          const evt = parseStreamLine(line);
          if (!evt) continue;
          const part = eventToContentPart(evt);
          if (part) onPart(part);
        }
      }
    } catch (error) {
      if (error instanceof Error) console.error(error.message);
      onPart({
        kind: 'text',
        text: 'An error occurred while processing the request. Please try again.'
      });
    }
  }

  const mutation = useMutation<void, unknown, SendMessageArgs>({
    mutationFn: streamMessage,
    onSettled: () => {
      abortRef.current = null;
    }
  });

  const cancel = () => {
    abortRef.current?.abort();
  };

  return { mutation, cancel };
}
